import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The one dialog. Every add / edit / confirm in the portal opens one of these —
 * never a section that appears inside the page.
 *
 *   <Modal title="Add a brand" onClose={close} onSubmit={save}
 *          footer={<ModalActions busy={busy} submitLabel="Add brand" onCancel={close} />}>
 *     …fields…
 *   </Modal>
 *
 * - Escape closes (unless `busy`); clicking the backdrop does NOT, so typing isn't lost.
 * - Focus moves into the dialog, Tab stays inside it, and returns where it was on close.
 * - With onSubmit the body is a <form>, so Enter submits.
 */
export default function Modal({ title, description, onClose, onSubmit, footer, children, size = 'md', busy = false, tone }) {
  const titleId = useId();
  const descId = useId();
  const panel = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    const previous = document.activeElement;
    document.body.classList.add('modal-open');
    // First field if there is one, otherwise the dialog itself
    const first = panel.current?.querySelector('input:not([type=hidden]), select, textarea');
    (first || panel.current)?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) { e.stopPropagation(); closeRef.current?.(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const [firstItem, lastItem] = [items[0], items[items.length - 1]];
      if (e.shiftKey && document.activeElement === firstItem) { e.preventDefault(); lastItem.focus(); }
      else if (!e.shiftKey && document.activeElement === lastItem) { e.preventDefault(); firstItem.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
      previous?.focus?.();
    };
  }, []);

  const Body = onSubmit ? 'form' : 'div';
  const bodyProps = onSubmit ? { onSubmit: (e) => { e.preventDefault(); if (!busy) onSubmit(e); }, noValidate: true } : {};

  return createPortal(
    <div className="modal-backdrop">
      <div ref={panel} className={`modal modal-${size}${tone ? ` modal-${tone}` : ''}`} role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={description ? descId : undefined} tabIndex={-1}>
        <Body className="modal-inner" {...bodyProps}>
          <header className="modal-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
          </header>
          <div className="modal-body">
            {description && <p id={descId} className="muted">{description}</p>}
            {children}
          </div>
          {footer && <footer className="modal-foot">{footer}</footer>}
        </Body>
      </div>
    </div>,
    document.body,
  );
}

/** Standard footer: Cancel + the main action. */
export function ModalActions({ submitLabel, busyLabel, busy, onCancel, danger = false, disabled = false, cancelLabel = 'Cancel' }) {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
      <button type="submit" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} disabled={busy || disabled}>
        {busy ? (busyLabel || `${submitLabel}…`) : submitLabel}
      </button>
    </>
  );
}
